import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import * as authApi from "@/api/authClient";

// Client-side store state: cart, wishlist, recently viewed. Authentication is
// REAL: a signed JWT issued by the KHA Auth mu-plugin on WordPress, stored in
// localStorage and validated against /kha/v1/me on load. The user shown here
// comes from the actual WordPress customer account.

const AppContext = createContext(null);
const load = (k, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(k)) ?? fallback;
  } catch {
    return fallback;
  }
};

export function AppProvider({ children }) {
  const [cart, setCart] = useState(() => load("kha_cart", []));
  const [savedForLater, setSaved] = useState(() => load("kha_saved", []));
  const [wishlist, setWishlist] = useState(() => load("kha_wishlist", []));
  const [recentlyViewed, setRecent] = useState(() => load("kha_recent", []));
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => { localStorage.setItem("kha_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("kha_saved", JSON.stringify(savedForLater)); }, [savedForLater]);
  useEffect(() => { localStorage.setItem("kha_wishlist", JSON.stringify(wishlist)); }, [wishlist]);
  useEffect(() => { localStorage.setItem("kha_recent", JSON.stringify(recentlyViewed)); }, [recentlyViewed]);

  // Restore a real session from the stored token (silent re-authentication).
  useEffect(() => {
    (async () => {
      if (authApi.getToken()) {
        try {
          const d = await authApi.me();
          setUser({ id: d.id, name: d.name, email: d.email });
        } catch {
          authApi.logout();
        }
      }
      setAuthReady(true);
    })();
  }, []);

  const lineKey = (id, variant) => `${id}::${JSON.stringify(variant || {})}`;

  const addToCart = useCallback((product, qty = 1, variant = null) => {
    setCart((prev) => {
      const key = lineKey(product.id, variant);
      const found = prev.find((l) => l.key === key);
      if (found) return prev.map((l) => (l.key === key ? { ...l, qty: l.qty + qty } : l));
      return [
        ...prev,
        {
          key,
          id: product.id,
          slug: product.slug,
          name: product.name,
          image: product.image,
          price: product.price,
          currency: product.currency,
          variant,
          qty,
        },
      ];
    });
  }, []);

  const updateQty = useCallback((key, qty) => {
    setCart((prev) => prev.map((l) => (l.key === key ? { ...l, qty: Math.max(1, qty) } : l)));
  }, []);
  const removeFromCart = useCallback((key) => setCart((prev) => prev.filter((l) => l.key !== key)), []);
  const clearCart = useCallback(() => setCart([]), []);

  const saveForLater = useCallback((key) => {
    setCart((prev) => {
      const line = prev.find((l) => l.key === key);
      if (line) setSaved((s) => (s.find((x) => x.key === key) ? s : [...s, line]));
      return prev.filter((l) => l.key !== key);
    });
  }, []);
  const moveToCart = useCallback((key) => {
    setSaved((prev) => {
      const line = prev.find((l) => l.key === key);
      if (line) setCart((c) => (c.find((x) => x.key === key) ? c : [...c, line]));
      return prev.filter((l) => l.key !== key);
    });
  }, []);

  const toggleWishlist = useCallback((product) => {
    setWishlist((prev) => {
      if (prev.find((p) => p.id === product.id)) return prev.filter((p) => p.id !== product.id);
      return [...prev, { id: product.id, slug: product.slug, name: product.name, image: product.image, price: product.price, currency: product.currency }];
    });
  }, []);
  const inWishlist = useCallback((id) => wishlist.some((p) => p.id === id), [wishlist]);

  const pushRecent = useCallback((product) => {
    setRecent((prev) => {
      const rest = prev.filter((p) => p.id !== product.id);
      return [{ id: product.id, slug: product.slug, name: product.name, image: product.image, price: product.price, currency: product.currency }, ...rest].slice(0, 12);
    });
  }, []);

  // Real session management (payload comes from the auth client, which stores
  // the JWT). logout() revokes the local session; the JWT simply expires.
  const login = useCallback((userData) => setUser({ id: userData.id, name: userData.name, email: userData.email }), []);
  const logout = useCallback(() => { authApi.logout(); setUser(null); }, []);
  const refreshUser = useCallback(async () => {
    const d = await authApi.me();
    setUser({ id: d.id, name: d.name, email: d.email });
    return d;
  }, []);

  const cartCount = useMemo(() => cart.reduce((n, l) => n + l.qty, 0), [cart]);
  const cartSubtotal = useMemo(() => cart.reduce((s, l) => s + (l.price || 0) * l.qty, 0), [cart]);

  const value = useMemo(
    () => ({
      cart, cartCount, cartSubtotal, addToCart, updateQty, removeFromCart, clearCart,
      savedForLater, saveForLater, moveToCart,
      wishlist, toggleWishlist, inWishlist,
      recentlyViewed, pushRecent,
      user, authReady, login, logout, refreshUser,
    }),
    [cart, cartCount, cartSubtotal, addToCart, updateQty, removeFromCart, clearCart, savedForLater, saveForLater, moveToCart, wishlist, toggleWishlist, inWishlist, recentlyViewed, pushRecent, user, authReady, login, logout, refreshUser]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
